import React, { useCallback, useEffect, useState } from 'react';
import { Wallet, CreditCard, History, PlusCircle, AlertCircle, CheckCircle2, Loader2, SendHorizonal, Building2, Store, MessageSquare } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import useAccess from '../../hooks/useAccess';
import { SMIIFY_API } from '../../config/api';
import styles from './WalletBilling.module.css';
import ex from './WalletExtras.module.css';

// =============================================================================
// Wallet of HQ or of the branch in the header.
// Recharge: Razorpay only (the money lands in THIS wallet). HQ can send credits to branches.
// Campaigns hold money while they send; "available" is what can be spent now.
// =============================================================================
const API = `${SMIIFY_API}/wallet`;
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KIND = {
    RECHARGE: ['Recharge', 'in'], TRANSFER_IN: ['Received', 'in'], TRANSFER_OUT: ['Sent', 'out'], HOLD: ['Reserved', 'hold'], RELEASE: ['Released', 'in'],
    CHARGE: ['Messages', 'out'], REFUND: ['Refund', 'in'], REIMBURSE_IN: ['From branch', 'in'], ADJUST: ['Adjust', 'out'],
};
const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
});

const WalletBilling = () => {
    const { activeStore, availableStores } = useStore();
    const { can, isOwner } = useAccess();
    const currentId = activeStore?._id || activeStore?.id;
    const isHQ = !activeStore || activeStore?.isHQ === true || currentId === 'global_hq';
    const storeId = isHQ ? 'global_hq' : String(currentId);
    const storeName = isHQ ? 'Global HQ' : (activeStore?.storeName || activeStore?.name || 'Branch');
    const canRecharge = isOwner || can('wallet.recharge');
    const canTransfer = isHQ && (isOwner || can('wallet.transfer'));

    const [w, setW] = useState(null);
    const [rows, setRows] = useState([]);
    const [nextBefore, setNextBefore] = useState(null);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState('');
    const [paying, setPaying] = useState(false);
    const [transfer, setTransfer] = useState({ to: '', amount: '' });
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState(null);
    const presets = [500, 1000, 2000, 5000];

    const headers = useCallback((json) => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId, ...(json ? { 'Content-Type': 'application/json' } : {}) }), [storeId]);
    const flash = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), type === 'success' ? 4000 : 7000); };

    const ready = Boolean(activeStore);
    const load = useCallback(async () => {
        if (!ready) return;
        setLoading(true);
        try {
            const [b, l] = await Promise.all([
                fetch(`${API}/balance`, { headers: headers() }).then((r) => r.json()),
                fetch(`${API}/ledger?limit=30`, { headers: headers() }).then((r) => r.json()),
            ]);
            if (!b.success) throw new Error(b.message || 'Could not load the wallet.');
            setW(b.data);
            setRows(l.success ? l.data : []);
            setNextBefore(l.nextBefore || null);
            if (isHQ) {
                const br = await fetch(`${API}/branches`, { headers: headers() }).then((r) => r.json()).catch(() => ({}));
                setBranches(br.success ? br.data : []);
            }
        } catch (e) { flash('error', e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setLoading(false); }
    }, [ready, headers, isHQ]);
    useEffect(() => { load(); }, [load]);

    const more = async () => {
        const l = await fetch(`${API}/ledger?limit=30&before=${encodeURIComponent(nextBefore)}`, { headers: headers() }).then((r) => r.json());
        if (l.success) { setRows((x) => [...x, ...l.data]); setNextBefore(l.nextBefore || null); }
    };

    const recharge = async (e) => {
        e.preventDefault();
        const amt = Math.round(Number(amount) || 0);
        const min = w?.recharge?.min || 100;
        if (amt < min) return flash('error', `Recharge at least ₹${min}.`);
        setPaying(true);
        try {
            if (!(await loadRazorpay())) throw new Error('Razorpay could not open. Check your internet.');
            const o = await fetch(`${API}/razorpay/order`, { method: 'POST', headers: headers(true), body: JSON.stringify({ amount: amt }) }).then((r) => r.json());
            if (!o.success) throw new Error(o.message);
            const d = o.data;
            await new Promise((resolve) => {
                const rzp = new window.Razorpay({
                    key: d.keyId, order_id: d.orderId, amount: d.amount, currency: d.currency, name: d.name, description: d.description, prefill: d.prefill,
                    theme: { color: '#673de6' },
                    handler: async (resp) => {
                        const v = await fetch(`${API}/razorpay/verify`, { method: 'POST', headers: headers(true), body: JSON.stringify(resp) }).then((r) => r.json()).catch(() => ({}));
                        if (v.success) { flash('success', v.message); setAmount(''); } else flash('error', v.message || 'Payment received but not confirmed yet. It will be added within a few minutes.');
                        resolve();
                    },
                    modal: { ondismiss: () => resolve() },
                });
                rzp.on('payment.failed', (r) => { flash('error', r?.error?.description || 'Payment failed. No money was taken.'); });
                rzp.open();
            });
            await load();
        } catch (err) { flash('error', err.message); } finally { setPaying(false); }
    };

    const sendCredits = async (e) => {
        e.preventDefault();
        if (!transfer.to || !(Number(transfer.amount) > 0)) return flash('error', 'Choose a branch and an amount.');
        setSending(true);
        try {
            const r = await fetch(`${API}/transfer`, { method: 'POST', headers: headers(true), body: JSON.stringify({ toStoreId: transfer.to, amount: Number(transfer.amount) }) }).then((x) => x.json());
            if (!r.success) throw new Error(r.message);
            flash('success', r.message);
            setTransfer({ to: '', amount: '' });
            await load();
        } catch (err) { flash('error', err.message); } finally { setSending(false); }
    };

    if (!activeStore) return <div className={styles.pageWrapper}><div className={styles.loaderWrap}><Loader2 size={32} className={styles.loaderIcon} /></div></div>;
    const gst = w?.recharge?.gstPercent ?? 18;
    const amt = Math.round(Number(amount) || 0);
    const branchList = branches.length ? branches : (availableStores || []).filter((s) => !s.isHQ).map((s) => ({ storeId: s._id || s.id, storeName: s.storeName || s.name }));

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.scrollContainer}>
                {toast && <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>{toast.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />} {toast.text}</div>}

                <div className={styles.pageHeader}>
                    <div className={styles.headerTitleWrap}>
                        {isHQ ? <Building2 size={20} className={styles.themeIcon} /> : <Store size={20} className={styles.themeIcon} />}
                        <div>
                            <h1 className={styles.title}>WhatsApp wallet</h1>
                            <p className={styles.subtitle}>{isHQ ? 'HQ master wallet and branch credits' : `Wallet of ${storeName}`}</p>
                        </div>
                    </div>
                </div>

                {w?.line && (
                    <div className={ex.lineBox}>
                        <MessageSquare size={15} />
                        <span>Line: <b>{w.line.label}</b></span>
                        <span>Marketing <b>{w.line.prices.MARKETING.total ? inr(w.line.prices.MARKETING.total) : 'free'}</b></span>
                        <span>Bills / utility <b>{w.line.prices.UTILITY.total ? inr(w.line.prices.UTILITY.total) : 'free'}</b></span>
                    </div>
                )}

                <div className={styles.grid}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}><Wallet size={14} className={styles.themeIcon} /><span>Balance</span></div>
                        {loading && !w ? <div className={styles.loaderWrap}><Loader2 size={24} className={styles.loaderIcon} /></div> : (
                            <>
                                <h2 className={styles.balanceAmount}>{inr(w?.available)}</h2>
                                <p className={styles.balanceDesc}>Available to spend · lifetime spent {inr(w?.metrics?.lifetimeSpent)}</p>
                                <div className={ex.split}>
                                    <div><span>Balance</span><b>{inr(w?.balance)}</b></div>
                                    <div className={ex.held}><span>Reserved</span><b>{inr(w?.held)}</b></div>
                                    <div><span>Messages sent</span><b>{(w?.metrics?.totalMessagesSent || 0).toLocaleString('en-IN')}</b></div>
                                </div>
                            </>
                        )}
                    </div>

                    {canRecharge && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}><CreditCard size={14} className={styles.themeIcon} /><span>Recharge {isHQ ? 'HQ' : 'this branch'} wallet</span></div>
                            {w && !w.recharge?.online ? <p className={styles.balanceDesc}>Online recharge is not set up yet. Contact Hirsta to add credits.</p> : (
                                <form onSubmit={recharge}>
                                    <div className={styles.inputGroup}>
                                        <div className={styles.inputWrapper}>
                                            <span className={styles.currencySymbol}>₹</span>
                                            <input type="number" className={styles.input} placeholder={`Amount (min ₹${w?.recharge?.min || 100})`} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={paying} min="1" />
                                        </div>
                                    </div>
                                    <div className={styles.presetGrid}>
                                        {presets.map((p) => <button key={p} type="button" className={`${styles.presetBtn} ${amt === p ? styles.presetBtnActive : ''}`} onClick={() => setAmount(String(p))} disabled={paying}>+₹{p}</button>)}
                                    </div>
                                    <button type="submit" className={styles.submitBtn} disabled={paying || !amt}>
                                        {paying ? <Loader2 size={12} className={styles.spin} /> : <PlusCircle size={12} />}
                                        <span>{amt ? `Pay ${inr(amt * (1 + gst / 100))}` : 'Pay with Razorpay'}</span>
                                    </button>
                                    {amt > 0 && <p className={ex.gstNote}>₹{amt.toLocaleString('en-IN')} credits + {gst}% GST ({inr(amt * gst / 100)}). Credits land in this wallet.</p>}
                                </form>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.grid}>
                    {canTransfer && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}><SendHorizonal size={14} className={styles.themeIcon} /><span>Send credits to a branch</span></div>
                            <form onSubmit={sendCredits} className={styles.transferForm}>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>Branch</label>
                                    <select className={styles.input} value={transfer.to} onChange={(e) => setTransfer((t) => ({ ...t, to: e.target.value }))} disabled={sending}>
                                        <option value="">Choose branch</option>
                                        {branchList.map((b) => <option key={b.storeId} value={b.storeId}>{b.storeName}{b.balance !== undefined ? ` (${inr(b.balance)})` : ''}</option>)}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label className={styles.label}>Amount</label>
                                    <div className={styles.inputWrapper}><span className={styles.currencySymbol}>₹</span><input type="number" className={styles.input} placeholder="0" value={transfer.amount} onChange={(e) => setTransfer((t) => ({ ...t, amount: e.target.value }))} disabled={sending} min="1" /></div>
                                </div>
                                <button type="submit" className={styles.submitBtn} disabled={sending || !transfer.to || !transfer.amount}>{sending ? <Loader2 size={12} className={styles.spin} /> : <SendHorizonal size={12} />}<span>Send credits</span></button>
                            </form>
                            {branches.length > 0 && (
                                <div className={styles.tableWrapper} style={{ marginTop: 14 }}>
                                    <table className={styles.historyTable}>
                                        <thead><tr><th>Branch</th><th style={{ textAlign: 'right' }}>Available</th><th style={{ textAlign: 'right' }}>Reserved</th></tr></thead>
                                        <tbody>
                                            {branches.map((b) => (
                                                <tr key={b.storeId} className={ex.branchRow} onClick={() => setTransfer((t) => ({ ...t, to: b.storeId }))} title="Send credits to this branch">
                                                    <td>{b.storeName}</td>
                                                    <td style={{ textAlign: 'right' }} className={b.available < 50 ? ex.low : ''}>{inr(b.available)}</td>
                                                    <td style={{ textAlign: 'right' }}>{inr(b.held)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    <div className={`${styles.card} ${styles.historyCard}`}>
                        <div className={styles.cardHeader}><History size={14} className={styles.themeIcon} /><span>Passbook</span></div>
                        {loading && !rows.length ? <div className={styles.loaderWrap}><Loader2 size={20} className={styles.loaderIcon} /></div> : (
                            <div className={styles.tableWrapper}>
                                <table className={styles.historyTable}>
                                    <thead><tr><th>Date</th><th>Entry</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
                                    <tbody>
                                        {rows.length === 0 ? <tr><td colSpan="4" style={{ textAlign: 'center', padding: 16, color: '#94a3b8' }}>No entries yet</td></tr> : rows.map((t) => {
                                            const [labelText, dir] = KIND[t.kind] || [t.kind, 'out'];
                                            return (
                                                <tr key={t._id}>
                                                    <td className={styles.dateCell}>{new Date(t.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                                                    <td><span className={`${ex.kind} ${dir === 'in' ? ex.kindIn : dir === 'hold' ? ex.kindHold : ex.kindOut}`}>{labelText}</span> {t.description}{t.byName ? ` · ${t.byName}` : ''}</td>
                                                    <td style={{ textAlign: 'right' }}>{dir === 'in' ? <span className={styles.creditText}>+{inr(t.amount)}</span> : dir === 'hold' ? <span>{inr(t.amount)}</span> : <span className={styles.debitText}>−{inr(t.amount)}</span>}</td>
                                                    <td style={{ textAlign: 'right' }}>{inr(t.balanceAfter)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {nextBefore && <button type="button" className={ex.more} onClick={more}>Show older</button>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletBilling;
